import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import UniversalNavbar from '../../components/ui/UniversalNavbar';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { LynkLockup } from '../../components/ui/LynkLogo';

const UserSignIn = () => {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation('auth');
  const [formData, setFormData] = useState({ email: '', password: '', rememberMe: false });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [showConfirmPrompt, setShowConfirmPrompt] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.email?.trim()) newErrors.email = t('signIn.emailRequired');
    else if (formData?.email?.includes('@') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) newErrors.email = t('signIn.emailInvalid');
    if (!formData?.password) newErrors.password = t('signIn.passwordRequired');
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setErrors({});
    setShowConfirmPrompt(false);
    setResendSent(false);
    try {
      const result = await signIn(formData?.email, formData?.password);
      if (result?.error) {
        const code = result?.error?.code;
        if (code === 'email_not_confirmed') {
          setShowConfirmPrompt(true);
        } else {
          setErrors({ form: result?.error?.message || t('signIn.invalidCredentials') });
        }
        setIsLoading(false);
        return;
      }
      const pendingConferenceId = sessionStorage?.getItem('pendingSubmissionConferenceId');
      if (pendingConferenceId) {
        sessionStorage?.removeItem('pendingSubmissionConferenceId');
        navigate(`/author-submission-portal?conferenceId=${pendingConferenceId}`);
      } else {
        navigate('/user-dashboard');
      }
    } catch (err) {
      setErrors({ form: t('signIn.unexpectedError') });
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    setIsResending(true);
    try {
      await supabase?.auth?.resend({
        type: 'signup',
        email: formData?.email?.trim()?.toLowerCase(),
        options: { emailRedirectTo: `${window.location.origin}/check-email?verified=1` }
      });
      setResendSent(true);
    } catch {
      // ignore — resend errors shouldn't block the user
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-background bg-mesh">
      <UniversalNavbar />
      <div className="flex items-center justify-center px-4 py-12 min-h-[calc(100vh-56px)]">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center mb-4">
              {/* Auth is one of the few surfaces with room for the full lockup,
                  and it's where the tagline does real work. */}
              <LynkLockup size={96} ink="var(--color-mark)" />
            </div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">{t('signIn.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('signIn.subtitle')}</p>
          </div>

          <div className="bg-card border border-border border-t-2 border-t-primary p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {showConfirmPrompt && (
                <div className="p-3 bg-warning/10 border border-warning/30 rounded-md">
                  <p className="text-xs font-medium text-foreground mb-1">Email not confirmed</p>
                  <p className="text-xs text-muted-foreground mb-2">
                    You need to confirm your email address before signing in. Check your inbox for the confirmation link.
                  </p>
                  {resendSent ? (
                    <p className="text-xs text-success font-medium">Confirmation email sent — check your inbox.</p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendConfirmation}
                      disabled={isResending}
                      className="text-xs text-primary hover:underline disabled:opacity-50"
                    >
                      {isResending ? 'Sending…' : 'Resend confirmation email'}
                    </button>
                  )}
                </div>
              )}

              {errors?.form && (
                <div className="p-2.5 bg-error/10 border border-error/20 rounded-md flex items-start gap-2">
                  <Icon name="AlertCircle" size={14} className="text-error flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-error">{errors?.form}</p>
                </div>
              )}

              <Input
                type="email"
                label={t('signIn.emailLabel')}
                placeholder={t('signIn.emailPlaceholder')}
                value={formData?.email}
                onChange={(e) => handleChange('email', e?.target?.value)}
                error={errors?.email}
                required
                autoComplete="email"
              />

              <div>
                <label className="text-xs font-medium text-foreground block mb-1.5">
                  {t('signIn.passwordLabel')} <span className="text-error">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('signIn.passwordPlaceholder')}
                    value={formData?.password}
                    onChange={(e) => handleChange('password', e?.target?.value)}
                    className={`w-full h-9 rounded-md border bg-background px-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors ${
                      errors?.password ? 'border-error' : 'border-input'
                    }`}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute ltr:right-2.5 rtl:left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={14} />
                  </button>
                </div>
                {errors?.password && <p className="text-xs text-error mt-1">{errors?.password}</p>}
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.rememberMe}
                    onChange={(e) => handleChange('rememberMe', e?.target?.checked)}
                    className="h-3.5 w-3.5 border-input"
                  />
                  <span className="text-xs text-muted-foreground">{t('signIn.rememberMe')}</span>
                </label>
                <button type="button" onClick={() => navigate('/forgot-password')} className="text-xs text-primary hover:underline">
                  {t('signIn.forgotPassword')}
                </button>
              </div>

              <Button type="submit" fullWidth loading={isLoading}>
                {t('signIn.submitButton')}
              </Button>
            </form>

          </div>

          <div className="mt-4 text-center space-y-2">
            <p className="text-xs text-muted-foreground">
              {t('signIn.noAccount')}{' '}
              <Link to="/user-registration" className="text-primary font-medium hover:underline">{t('signIn.signUpLink')}</Link>
            </p>
            <button onClick={() => navigate('/public-conference-board')} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
              {t('signIn.browseAsGuest')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSignIn;
